import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { Box } from 'reflexbox';

import * as graphql from 'generated/graphql';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

import DetailsTableForm from '../../../../kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../../kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import { SubSectionTitle } from '../../../../kakapo-ui-kit/components/common/Headings';
import HorizontalRule from '../../../../kakapo-ui-kit/components/common/HorizontalRule';
import { ParagraphSmall } from '../../../../kakapo-ui-kit/components/common/Paragraph';

import MomentTemplateVariations from './MomentTemplateVariations';

export const MomentTemplateEditorFeedbackSettings = ({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) => {
	const { t } = useTranslation();

	const { data } = graphql.useFeedbackTemplatesQuery({
		variables: {
			isArchived: false,
		},
	});

	const feedbackTemplates = data?.feedbackTemplates.edges
		.map((edge) => ({
			label: edge.node.title[0].text,
			value: edge.node.id,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	return (
		<DetailsTableForm
			formValues={[
				{
					label: t('common.settings.momentTemplates.edit.feedbackTemplate'),
					name: `${name}.feedback.feedbackTemplate.id`,
					type: DetailsTableFormValueTypes.Select,
					options: feedbackTemplates,
				},
				{
					label: t('common.settings.momentTemplates.edit.minimumQuestions'),
					name: `${name}.feedback.minimumQuestions`,
					type: DetailsTableFormValueTypes.Number,
				},
				{
					label: t('common.settings.momentTemplates.edit.minimumRecipients'),
					name: `${name}.feedback.minimumRecipients`,
					type: DetailsTableFormValueTypes.Number,
				},
				{
					label: t('common.settings.momentTemplates.edit.maximumRecipients'),
					name: `${name}.feedback.maximumRecipients`,
					type: DetailsTableFormValueTypes.Number,
				}
			]}
		/>
	);
};

export const MomentTemplateEditorGoalSettings = ({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) => {
	const { t } = useTranslation();

	const [{ value: totalGoals }] = useField(`${name}.goals.minimumGoals`);
	const [{ value: competencyGoals }] = useField(`${name}.goals.minimumCompetencyGoals`);
	const [{ value }] = useField(`${name}.goals.allowCompetencyGoals`);

	const hasCompetencyGoals = useFeature('2021.competencyGoals');

	return (
		<>
			<DetailsTableForm
				formValues={[
					{
						label: t('common.settings.momentTemplates.edit.minimumGoals'),
						name: `${name}.goals.minimumGoals`,
						type: DetailsTableFormValueTypes.Number,
					},
					...(hasCompetencyGoals
						? [
								{
									label: t('common.settings.momentTemplates.edit.allowCompetencyGoals'),
									name: `${name}.goals.allowCompetencyGoals`,
									type: DetailsTableFormValueTypes.Switch,
								},
						  ]
						: []),
					...(value
						? [
								{
									label: t('common.settings.momentTemplates.edit.minimumCompetencyGoals'),
									name: `${name}.goals.minimumCompetencyGoals`,
									type: DetailsTableFormValueTypes.Number,
								},
						  ]
						: []),
				]}
			/>
			{value && competencyGoals > totalGoals ? (
				<ParagraphSmall color="red">
					{t('common.settings.momentTemplates.edit.competencyGoalsCountInconsistency', {
						competencyGoals,
						totalGoals,
					})}
				</ParagraphSmall>
			) : (
				value && (
					<ParagraphSmall color="slateBlue">
						{t('common.settings.momentTemplates.edit.competencyGoalsCountExplanation', {
							competencyGoals: competencyGoals || 0,
							totalGoals,
							regularGoals: totalGoals - competencyGoals,
						})}
					</ParagraphSmall>
				)
			)}
		</>
	);
};

export const MomentTemplateEditorEvaluationSettings = ({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) => {
	const { data: evaluationFormsData } = graphql.useEvaluationFormsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const { data: jobTitlesData } = graphql.useJobTitlesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const evaluationForms = evaluationFormsData?.evaluationForms.edges
		.map((edge) => ({
			label: edge.node.title[0].text,
			value: edge.node.id,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	const jobTitles = jobTitlesData?.jobTitles.edges
		.map((edge) => ({
			label: edge.node.title[0].text,
			value: edge.node.id,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	return (
		<>
			{evaluationForms && jobTitles && (
				<MomentTemplateVariations
					defaultTemplateName={`${name}.evaluation.evaluationForm.id`}
					name={`${name}.variations`}
					templates={evaluationForms}
					jobTitles={jobTitles}
				/>
			)}
		</>
	);
};

export const MomentTemplateEditorMessageSettings = ({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) => {
	const { t } = useTranslation();

	return (
		<DetailsTableForm
			formValues={[
				{
					label: t('common.settings.momentTemplates.edit.showToUser'),
					name: `${name}.showToUser`,
					type: DetailsTableFormValueTypes.Switch,
				},
			]}
		/>
	);
};

export const MomentTemplateEditorTalentReviewSettings = ({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) => {
	const { t } = useTranslation();
	return (
		<ParagraphSmall color="lightBlueDark">
			{t('common.settings.momentTemplates.edit.noOptions')}
		</ParagraphSmall>
	);
};

const mapping = {
	[graphql.MomentType.Evaluation]: MomentTemplateEditorEvaluationSettings,
	[graphql.MomentType.Feedback]: MomentTemplateEditorFeedbackSettings,
	[graphql.MomentType.Goals]: MomentTemplateEditorGoalSettings,
	[graphql.MomentType.Message]: MomentTemplateEditorMessageSettings,
	[graphql.MomentType.TalentReview]: MomentTemplateEditorTalentReviewSettings,
};

export default function MomentTemplateEditorTypeSettings({
	id,
	name,
	type,
}: {
	id: string;
	name: string;
	type: graphql.MomentType;
}) {
	const { t } = useTranslation();

	const Component = mapping[type];

	return (
		<>
			<Box mt={2} mb={3}>
				<Box mb={2}>
					<SubSectionTitle>
						{t(`common.settings.cycleTemplates.momentTypes.${type.toLowerCase()}`)}&nbsp;
						{t('common.settings.momentTemplates.edit.details').toLowerCase()}
					</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<Component id={id} type={type} name={name} />
		</>
	);
}
